import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Event from app_news_with_events view
export interface PricingEvent {
  id: string;
  news_id: string;
  page_id: string;
  slug: string;
  v_before: string;
  v_after: string;
  hook?: string;
  news_description?: string;
  event_description?: string;
  category?: string;
  event_type?: string;
  published?: boolean;
  created_at?: string;
  updated_at?: string;
  period?: string;
  delta?: number;
  verified?: boolean;
  partially_correct?: boolean;
  invalid?: boolean;
  asset_id?: string;
  tags?: string[];
  asset_ids?: string[];
  labels?: string[];
  annotation_coordinates?: any;
  primary_cropped_url?: string;
  secondary_cropped_url?: string;
  primary_original_url?: string;
  secondary_original_url?: string;
}

// Grouped by quarter
export interface QuarterData {
  quarter: string;
  period: string;
  events: PricingEvent[];
}

// Grouped by category within a quarter
export interface CategorySection {
  category: string;
  events: PricingEvent[];
}

interface UseCompanyPricingHistoryResult {
  events: PricingEvent[];
  quarterData: QuarterData[];
  weekData: QuarterData[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch pricing history events for a company
 * Data source: app_news_with_events view (same as /diffs page)
 *
 * @param slug - Company slug (e.g., 'konghq')
 * @param options - Optional filters (e.g., published status)
 */
export function useCompanyPricingHistory(
  slug: string | null,
  options?: { published?: boolean }
): UseCompanyPricingHistoryResult {
  const [events, setEvents] = useState<PricingEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async () => {
    if (!slug) {
      setEvents([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Note: We only filter by slug, not page_id, because Management API page IDs
      // don't match the internal database page_ids in app_news_with_events
      let query = supabase
        .from('app_news_with_events')
        .select('*')
        .eq('slug', slug)
        .order('created_at', { ascending: false });

      // Optionally filter by published status
      if (options?.published !== undefined) {
        query = query.eq('published', options.published);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw fetchError;
      }

      // Map the view data to our PricingEvent interface
      const mappedEvents: PricingEvent[] = (data || []).map((row: any) => ({
        id: row.id,
        news_id: row.news_id || row.id,
        page_id: row.page_id,
        slug: row.slug,
        v_before: row.v_before,
        v_after: row.v_after,
        hook: row.hook,
        news_description: row.news_description,
        event_description: row.description,
        category: row.category,
        event_type: row.event_type,
        published: row.published,
        created_at: row.created_at,
        updated_at: row.updated_at,
        period: row.period,
        delta: row.delta,
        verified: row.verified,
        partially_correct: row.partially_correct,
        invalid: row.invalid,
        asset_id: row.asset_id,
        tags: parsePostgresArray(row.tags),
        asset_ids: parsePostgresArray(row.asset_ids),
        labels: parsePostgresArray(row.labels),
        annotation_coordinates: row.annotation_coordinates,
        primary_cropped_url: row.primary_cropped_url,
        secondary_cropped_url: row.secondary_cropped_url,
        primary_original_url: row.primary_original_url,
        secondary_original_url: row.secondary_original_url,
      }));

      setEvents(mappedEvents);
    } catch (err) {
      console.error('[useCompanyPricingHistory] Error fetching events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch pricing history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [slug, options?.published]);

  // Separate events into quarters and weeks
  const { quarterData, weekData } = groupEventsByPeriodType(events);

  return {
    events,
    quarterData,
    weekData,
    loading,
    error,
    refetch: fetchEvents,
  };
}

/**
 * Helper to parse PostgreSQL array string format
 */
function parsePostgresArray(value: any): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  // PostgreSQL array format: "{item1,item2,item3}"
  if (typeof value === 'string' && value.startsWith('{') && value.endsWith('}')) {
    const content = value.slice(1, -1);
    if (!content) return [];
    return content.split(',').map(item => item.trim());
  }

  return [];
}

/**
 * Check if a period is a quarter format (e.g., "2025Q1")
 */
function isQuarterPeriod(period: string): boolean {
  return /^\d{4}Q\d$/.test(period);
}

/**
 * Check if a period is a week format (e.g., "2025W47")
 */
function isWeekPeriod(period: string): boolean {
  return /^\d{4}W\d+$/.test(period);
}

/**
 * Group events by period type (quarters vs weeks)
 */
function groupEventsByPeriodType(events: PricingEvent[]): { quarterData: QuarterData[]; weekData: QuarterData[] } {
  const quarterMap = new Map<string, PricingEvent[]>();
  const weekMap = new Map<string, PricingEvent[]>();

  for (const event of events) {
    const period = event.period || 'Unknown';

    if (isQuarterPeriod(period)) {
      if (!quarterMap.has(period)) {
        quarterMap.set(period, []);
      }
      quarterMap.get(period)!.push(event);
    } else if (isWeekPeriod(period)) {
      if (!weekMap.has(period)) {
        weekMap.set(period, []);
      }
      weekMap.get(period)!.push(event);
    }
    // Skip unknown/custom formats
  }

  // Convert to arrays and sort by period (newest first)
  const quarterData: QuarterData[] = Array.from(quarterMap.entries())
    .map(([period, events]) => ({
      quarter: formatQuarterLabel(period),
      period,
      events,
    }))
    .sort((a, b) => b.period.localeCompare(a.period));

  const weekData: QuarterData[] = Array.from(weekMap.entries())
    .map(([period, events]) => ({
      quarter: formatQuarterLabel(period),
      period,
      events,
    }))
    .sort((a, b) => b.period.localeCompare(a.period));

  return { quarterData, weekData };
}

/**
 * Format period string to display label
 * e.g., "2025Q1" -> "Q1 2025"
 */
function formatQuarterLabel(period: string): string {
  // Handle formats like "2025Q1", "2025W47", etc.
  const yearMatch = period.match(/^(\d{4})/);
  const quarterMatch = period.match(/Q(\d)/);
  const weekMatch = period.match(/W(\d+)/);

  if (yearMatch && quarterMatch) {
    return `Q${quarterMatch[1]} ${yearMatch[1]}`;
  }

  if (yearMatch && weekMatch) {
    return `Week ${weekMatch[1]}, ${yearMatch[1]}`;
  }

  return period;
}

/**
 * Group events within a quarter by category
 */
export function groupEventsByCategory(events: PricingEvent[]): CategorySection[] {
  const categoryMap = new Map<string, PricingEvent[]>();

  for (const event of events) {
    const category = event.category || 'misc';
    if (!categoryMap.has(category)) {
      categoryMap.set(category, []);
    }
    categoryMap.get(category)!.push(event);
  }

  // Sort categories by priority
  const categoryPriority: Record<string, number> = {
    pricing_structure: 1,
    feature: 2,
    limits: 3,
    misc: 4,
  };

  return Array.from(categoryMap.entries())
    .map(([category, events]) => ({ category, events }))
    .sort((a, b) => {
      const priorityA = categoryPriority[a.category] || 999;
      const priorityB = categoryPriority[b.category] || 999;
      return priorityA - priorityB;
    });
}

export default useCompanyPricingHistory;
